/**
 * FinanzenTab — Tab 2 im FahrzeugDetailDrawer
 *
 * Read-only Anzeige aller Finanzfelder eines Fahrzeugs. Holt bei Mount einen
 * kompakten Ausschnitt der `fahrzeuge`-Zeile (nur Finanzfelder) und rendert
 * sie via `buildFinanzenRows` als Label/Value-Grid. Skeleton-Loader während
 * Fetch, Leer-State wenn alle Felder leer sind.
 *
 * Part of Issue #186 / Parent #183.
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { buildFinanzenRows, type FahrzeugFinanzen } from '@/lib/finanzen-tab';

interface FinanzenTabProps {
  fahrzeug: { id: string };
}

const FINANZEN_COLUMNS = [
  'kaufpreis_netto',
  'kaufpreis_brutto',
  'bruttolistenpreis',
  'anschaffungsdatum',
  'finanzierungspartner',
  'darlehensnummer',
  'monatliche_rate',
  'restschuld',
  'laufzeitende',
  'afa_beginn',
  'afa_nutzungsdauer_jahre',
  'restbuchwert',
  'buchhaltungskonto',
].join(', ');

export default function FinanzenTab({ fahrzeug }: FinanzenTabProps) {
  const [loading, setLoading] = useState(true);
  const [finanzen, setFinanzen] = useState<FahrzeugFinanzen | null>(null);

  const fetchFinanzen = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('fahrzeuge')
      .select(FINANZEN_COLUMNS)
      .eq('id', fahrzeug.id)
      .maybeSingle();
    if (error) {
      console.error('Finanzen-Tab: fetch failed', error);
      toast.error('Fehler beim Laden der Finanzdaten');
      setFinanzen(null);
      setLoading(false);
      return;
    }
    setFinanzen((data as unknown as FahrzeugFinanzen) ?? null);
    setLoading(false);
  }, [fahrzeug.id]);

  useEffect(() => {
    void fetchFinanzen();
  }, [fetchFinanzen]);

  if (loading) {
    return (
      <div className="space-y-2" data-testid="finanzen-skeleton">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-8 rounded-lg bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  const rows = finanzen ? buildFinanzenRows(finanzen) : [];

  if (rows.length === 0) {
    return (
      <div
        className="text-center py-12 text-sm text-gray-400"
        data-testid="finanzen-empty"
      >
        Keine Finanzdaten für dieses Fahrzeug hinterlegt
      </div>
    );
  }

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="finanzen-grid">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
            {row.label}
          </dt>
          <dd className="text-sm text-gray-700 font-medium">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
